import jsPDF from 'jspdf';
import type Konva from 'konva';
import { collectSceneImageIds, sceneContentBounds, sceneToSvg, type SceneDoc } from '@system-atlas/shared';

export type ExportFormat = 'png' | 'svg' | 'pdf';

const EXPORT_PADDING = 24;
const PIXEL_RATIO = 2;

function triggerDownload(href: string, filename: string) {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Setzt die Stage vorübergehend auf die gesamte Szene (Zoom 1, Inhalt eingepasst),
 * ruft `fn` und stellt den Viewport danach wieder her.
 */
function withFullStage<T>(stage: Konva.Stage, scene: SceneDoc, fn: (bounds: ReturnType<typeof sceneContentBounds>) => T): T {
  const prev = {
    scaleX: stage.scaleX(),
    scaleY: stage.scaleY(),
    x: stage.x(),
    y: stage.y(),
    width: stage.width(),
    height: stage.height(),
  };
  const bounds = sceneContentBounds(scene, EXPORT_PADDING);
  stage.scale({ x: 1, y: 1 });
  stage.position({ x: -bounds.minX, y: -bounds.minY });
  stage.size({ width: Math.ceil(bounds.width), height: Math.ceil(bounds.height) });
  stage.draw();
  try {
    return fn(bounds);
  } finally {
    stage.scale({ x: prev.scaleX, y: prev.scaleY });
    stage.position({ x: prev.x, y: prev.y });
    stage.size({ width: prev.width, height: prev.height });
    stage.draw();
  }
}

export function exportScenePng(stage: Konva.Stage, scene: SceneDoc, filename: string) {
  const dataUrl = withFullStage(stage, scene, () => stage.toDataURL({ pixelRatio: PIXEL_RATIO, mimeType: 'image/png' }));
  triggerDownload(dataUrl, `${filename}.png`);
}

/** Lädt eine Ressource und wandelt sie in eine base64-Data-URI (für eigenständige SVGs). */
async function fetchAsDataUri(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Exportiert die Szene als eigenständiges SVG. Referenzierte Bilder werden als
 * base64-Data-URI eingebettet, damit die Datei außerhalb der App (Browser, Office,
 * Inkscape) die Bilder zeigt. Schlägt das Laden eines Bildes fehl, bleibt für dieses
 * der Platzhalter — der Export bricht nicht ab.
 */
export async function exportSceneSvg(scene: SceneDoc, filename: string, rawUrl?: (id: string) => string) {
  const hrefMap = new Map<string, string>();
  if (rawUrl) {
    await Promise.all(
      collectSceneImageIds(scene).map(async (id) => {
        try {
          hrefMap.set(id, await fetchAsDataUri(rawUrl(id)));
        } catch {
          /* Bild überspringen -> Platzhalter im SVG */
        }
      }),
    );
  }
  const svg = sceneToSvg(scene, { imageHref: (id) => hrefMap.get(id) ?? '' });
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, `${filename}.svg`);
  URL.revokeObjectURL(url);
}

export function exportScenePdf(stage: Konva.Stage, scene: SceneDoc, filename: string) {
  const bounds = sceneContentBounds(scene, EXPORT_PADDING);
  const dataUrl = withFullStage(stage, scene, () => stage.toDataURL({ pixelRatio: PIXEL_RATIO, mimeType: 'image/png' }));
  const landscape = bounds.width >= bounds.height;
  const pdf = new jsPDF({ orientation: landscape ? 'landscape' : 'portrait', unit: 'pt', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 24;
  const ratio = Math.min((pageW - margin * 2) / bounds.width, (pageH - margin * 2) / bounds.height);
  const w = bounds.width * ratio;
  const h = bounds.height * ratio;
  const x = (pageW - w) / 2;
  const y = (pageH - h) / 2;
  // Weisser Untergrund: transparente PNG-Raender (Padding ausserhalb des Blatts) wuerden in
  // jsPDF sonst schwarz erscheinen (z. B. als Linie am oberen Rand).
  pdf.setFillColor(255, 255, 255);
  pdf.rect(x, y, w, h, 'F');
  pdf.addImage(dataUrl, 'PNG', x, y, w, h);
  pdf.save(`${filename}.pdf`);
}
